import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Sparkles, Check, Plus, Trash2, Users, User, Wallet, PiggyBank, Ticket, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import api, { joinFamily, validateInviteCode } from '../lib/api';
import { toast } from 'sonner';

const AI_PERSONALITIES = [
  { id: 'pote', emoji: '😎', name_fr: 'Pote', name_en: 'Buddy', desc_fr: 'Fun, casual, emojis', desc_en: 'Fun, casual, emojis' },
  { id: 'coach', emoji: '💪', name_fr: 'Coach', name_en: 'Coach', desc_fr: 'Motivant, direct', desc_en: 'Motivating, direct' },
  { id: 'bienveillant', emoji: '🤗', name_fr: 'Bienveillant', name_en: 'Caring', desc_fr: 'Empathique, doux', desc_en: 'Empathetic, gentle' },
  { id: 'data_analyst', emoji: '📊', name_fr: 'Data Analyst', name_en: 'Analyst', desc_fr: 'Factuel, chiffres', desc_en: 'Factual, numbers' },
  { id: 'sarcastic', emoji: '😏', name_fr: 'Sarcastic', name_en: 'Sarcastic', desc_fr: 'Humour, piques', desc_en: 'Humor, teasing' },
];

const SITUATIONS = [
  { id: 'single', emoji: '👤', name_fr: 'Célibataire', name_en: 'Single', name_pt: 'Solteiro(a)' },
  { id: 'couple', emoji: '👫', name_fr: 'En couple', name_en: 'Couple', name_pt: 'Em casal' },
  { id: 'family', emoji: '👨‍👩‍👧‍👦', name_fr: 'Famille', name_en: 'Family', name_pt: 'Família' },
];

const BUDGET_MODES = [
  { 
    id: 'shared', 
    emoji: '🤝', 
    name_fr: 'Revenus partagés', 
    name_en: 'Shared income',
    name_pt: 'Rendimentos partilhados',
    desc_fr: 'Tout est mis en commun',
    desc_en: 'Everything is pooled together',
    desc_pt: 'Tudo é partilhado'
  },
  { 
    id: 'separate', 
    emoji: '👤', 
    name_fr: 'Revenus séparés', 
    name_en: 'Separate income',
    name_pt: 'Rendimentos separados',
    desc_fr: 'Chacun gère son budget',
    desc_en: 'Everyone manages their own',
    desc_pt: 'Cada um gere o seu'
  },
  { 
    id: 'hybrid', 
    emoji: '🏦', 
    name_fr: 'Hybride (Pot Commun)', 
    name_en: 'Hybrid (Common Pot)',
    name_pt: 'Híbrido (Pote Comum)',
    desc_fr: 'Revenus séparés + pot commun',
    desc_en: 'Separate income + common pot',
    desc_pt: 'Rendimentos separados + pote comum'
  },
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, updateProfile, checkAuth } = useAuth();
  const { t, language } = useLanguage();
  
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasInviteCode, setHasInviteCode] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [inviteError, setInviteError] = useState('');
  const [joinedFamily, setJoinedFamily] = useState(null);
  const [memberLimitError, setMemberLimitError] = useState(null); // For subscription limit errors
  // State for validated family info (before joining) - must be declared before getSteps
  const [validatedFamily, setValidatedFamily] = useState(null);
  
  // Refresh user data from auth context
  const refreshUser = async () => {
    try {
      await checkAuth();
    } catch (e) {
      console.error('Failed to refresh user:', e);
    }
  };
  
  // Handle complete onboarding for children (single screen)
  const handleCompleteOnboarding = async () => {
    setIsSubmitting(true);
    try {
      // Mark onboarding as completed
      await updateProfile({ onboarding_completed: true });
      
      toast.success(t('welcome_exclaim'));
      
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
      toast.error(t('an_error_occurred'));
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Check for invite code from URL state or sessionStorage on mount
  useEffect(() => {
    // Check location state first (from /join redirect)
    const stateCode = location.state?.inviteCode;
    // Then check sessionStorage (fallback)
    const storedCode = sessionStorage.getItem('spenspot_invite_code');
    
    const code = stateCode || storedCode;
    
    if (code) {
      setInviteCode(code);
      setHasInviteCode(true);
      // Clear from sessionStorage after use
      sessionStorage.removeItem('spenspot_invite_code');
      
      // Auto-validate and join for children
      autoJoinWithCode(code);
    }
  }, [location.state]);
  
  // Auto-validate and join with invite code (especially for children)
  const autoJoinWithCode = async (code) => {
    try {
      setIsSubmitting(true);
      setMemberLimitError(null);
      // First validate
      const validateRes = await validateInviteCode(code);
      setValidatedFamily(validateRes.data);
      
      // If child role, auto-join immediately
      if (validateRes.data.role === 'child') {
        try {
          const joinRes = await joinFamily(code);
          setJoinedFamily(joinRes.data);
          // Update auth context
          await refreshUser();
        } catch (joinError) {
          console.error('Auto-join failed:', joinError);
          // Check if it's a member limit error
          const errorDetail = joinError.response?.data?.detail;
          if (errorDetail?.code === 'MEMBER_LIMIT_REACHED') {
            setMemberLimitError(errorDetail);
          }
          // If join fails, still show the validated family info
        }
      }
    } catch (error) {
      console.error('Auto-validate failed:', error);
      // Don't show error, user can still manually enter code
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Determine steps based on situation and invite code
  const getSteps = () => {
    // If user has an invite code flow
    if (hasInviteCode) {
      // Check if validatedFamily shows user will be a child
      const isChildRole = validatedFamily?.role === 'child' || joinedFamily?.role === 'child' || joinedFamily?.is_child;
      
      if (isChildRole) {
        // Children: ultra-simplified flow - just welcome and finish
        return ['child_welcome'];
      }
      
      if (joinedFamily) {
        // After joining - just enter salary/city and personality
        return ['welcome', 'invite_code', 'invite_confirm', 'invite_info', 'personality', 'finish'];
      }
      if (validatedFamily) {
        // Code validated, showing confirmation with privacy info
        return ['welcome', 'invite_code', 'invite_confirm', 'invite_info', 'personality', 'finish'];
      }
      // Just entering the code
      return ['welcome', 'invite_code', 'invite_confirm', 'invite_info', 'personality', 'finish'];
    }
    
    const baseSteps = ['welcome', 'salary', 'city', 'situation'];
    if (formData.situation === 'couple' || formData.situation === 'family') {
      return [...baseSteps, 'family_setup', 'budget_mode', 'personality', 'finish'];
    }
    return [...baseSteps, 'personality', 'finish'];
  };

  const [formData, setFormData] = useState({
    salary: 2500,
    income_type: 'employed',
    city: '',
    situation: 'single',
    ai_personality: 'pote',
    // Family data
    family_name: '',
    budget_mode: 'shared',
    family_members: [],
    // For hybrid mode
    my_pot_contribution: 0,
  });

  const steps = getSteps();

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  // Handle validating invite code (shows family info with privacy warning)
  const handleValidateCode = async () => {
    if (!inviteCode.trim() || !formData.invite_name?.trim()) {
      setInviteError(t('enter_name_and_code'));
      return;
    }
    
    setIsSubmitting(true);
    setInviteError('');
    
    try {
      const response = await api.get(`/family/validate-invite/${inviteCode.trim()}`);
      setValidatedFamily(response.data);
      // Move to confirmation step
      setCurrentStep(prev => prev + 1);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || (t('invalid_code'));
      setInviteError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle actually joining the family after confirmation
  const handleConfirmJoinFamily = async () => {
    setIsSubmitting(true);
    setMemberLimitError(null);
    
    try {
      const response = await joinFamily(inviteCode.trim());
      setJoinedFamily(response.data);
      toast.success(response.data.message || t('family_joined'));
      // Move to salary/info step
      setCurrentStep(prev => prev + 1);
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      
      // Check if it's a member limit error (object with code)
      if (typeof errorDetail === 'object' && errorDetail?.code === 'MEMBER_LIMIT_REACHED') {
        setMemberLimitError(errorDetail);
        toast.error(errorDetail.message);
      } else {
        // Regular string error
        const errorMsg = typeof errorDetail === 'string' ? errorDetail : (t('error'));
        toast.error(errorMsg);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const addFamilyMember = () => {
    setFormData(prev => ({
      ...prev,
      family_members: [
        ...prev.family_members,
        { 
          id: Date.now(), 
          name: '', 
          role: 'adult', 
          salary: 0, 
          pot_contribution: 0,
          pocket_money: 0 
        }
      ]
    }));
  };

  const removeFamilyMember = (id) => {
    setFormData(prev => ({
      ...prev,
      family_members: prev.family_members.filter(m => m.id !== id)
    }));
  };

  const updateFamilyMember = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      family_members: prev.family_members.map(m => 
        m.id === id ? { ...m, [field]: value } : m
      )
    }));
  };

  const getTotalHouseholdIncome = () => {
    const myIncome = formData.salary || 0;
    const membersIncome = formData.family_members
      .filter(m => m.role === 'adult')
      .reduce((sum, m) => sum + (m.salary || 0), 0);
    return myIncome + membersIncome;
  };

  const getTotalPotCommun = () => {
    const myContribution = formData.my_pot_contribution || 0;
    const membersContribution = formData.family_members
      .filter(m => m.role === 'adult')
      .reduce((sum, m) => sum + (m.pot_contribution || 0), 0);
    return myContribution + membersContribution;
  };

  const handleFinish = async () => {
    setIsSubmitting(true);
    try {
      // For invited users who joined a family
      if (hasInviteCode && joinedFamily) {
        await updateProfile({
          salary: formData.income_type === 'employed' ? formData.salary : 0,
          income_type: formData.income_type,
          city: formData.city,
          ai_personality: formData.ai_personality,
          onboarding_completed: true,
          // situation is already set by the backend when joining
        });
        await checkAuth(); // Refresh user data
        navigate('/dashboard');
        return;
      }
      
      // Update user profile
      await updateProfile({
        salary: formData.income_type === 'employed' ? formData.salary : 0,
        income_type: formData.income_type,
        city: formData.city,
        situation: formData.situation,
        ai_personality: formData.ai_personality,
        onboarding_completed: true,
      });

      // Create family if couple/family
      if (formData.situation !== 'single' && formData.family_name) {
        // Calculate total pot commun target
        const totalPotCommun = formData.budget_mode === 'hybrid' 
          ? (formData.my_pot_contribution || 0) + formData.family_members
              .filter(m => m.role === 'adult')
              .reduce((sum, m) => sum + (m.pot_contribution || 0), 0)
          : 0;
        
        const familyData = {
          name: formData.family_name,
          budget_mode: formData.budget_mode,
          owner_pot_contribution: formData.budget_mode === 'hybrid' ? formData.my_pot_contribution : null,
          pot_commun_monthly_target: totalPotCommun || null,
          members: formData.family_members.map(m => ({
            name: m.name,
            email: m.email || null,  // Include email for invitation
            role: m.role,
            salary: null,  // Each member will set their own salary
            pot_commun_contribution: formData.budget_mode === 'hybrid' ? m.pot_contribution : null,
            pocket_money_monthly: m.role === 'child' ? m.pocket_money : null,
          }))
        };
        
        const response = await api.post('/family', familyData);
        
        // Log invitations sent
        if (response.data.invitations_sent && response.data.invitations_sent.length > 0) {
          console.log('Invitations sent:', response.data.invitations_sent);
        }
      }

      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to save profile:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    const step = steps[currentStep];
    
    switch (step) {
      case 'welcome':
        return (
          <div className="text-center">
            <div className="w-24 h-24 rounded-3xl bg-primary flex items-center justify-center mx-auto mb-8 animate-pulse-glow">
              <Sparkles className="w-12 h-12 text-white" />
            </div>
            <h1 className="text-4xl font-bold mb-4">{t('onboarding_welcome')}</h1>
            <p className="text-lg text-muted-foreground max-w-md mx-auto mb-8">
              {t('setup_profile_desc')}
            </p>
            
            {/* Invite Code Option */}
            {!hasInviteCode ? (
              <button
                onClick={() => setHasInviteCode(true)}
                className="inline-flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
              >
                <Ticket className="w-5 h-5" />
                <span className="underline underline-offset-2">
                  {t('have_invite_code')}
                </span>
              </button>
            ) : (
              <button
                onClick={() => {
                  setHasInviteCode(false);
                  setInviteCode('');
                  setInviteError('');
                  setJoinedFamily(null);
                }}
                className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>
                  {t('create_own_account')}
                </span>
              </button>
            )}
          </div>
        );

      case 'invite_code':
        return (
          <div className="text-center max-w-md mx-auto">
            <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Ticket className="w-10 h-10 text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-2">
              {t('join_household')}
            </h2>
            <p className="text-muted-foreground mb-8">
              {t('enter_name_and_code')}
            </p>
            
            <div className="space-y-4">
              {/* Name field */}
              <div>
                <label className="text-sm font-medium mb-2 block text-left">
                  {t('your_first_name')}
                </label>
                <Input
                  placeholder={t('example_name')}
                  value={formData.invite_name || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, invite_name: e.target.value }))}
                  className="text-lg py-5"
                  data-testid="invite-name-input"
                />
              </div>
              
              {/* Invite code field */}
              <div>
                <label className="text-sm font-medium mb-2 block text-left">
                  {t('invitation_code')}
                </label>
                <Input
                  placeholder="INV-XXXXXX-XXXXXXXX"
                  value={inviteCode}
                  onChange={(e) => {
                    setInviteCode(e.target.value.toUpperCase());
                    setInviteError('');
                  }}
                  className="text-center text-xl font-mono tracking-wider py-5 uppercase"
                  maxLength={20}
                  data-testid="invite-code-input"
                />
              </div>
              
              <p className="text-xs text-muted-foreground">
                {t('invite_code_info')}
              </p>
              
              {inviteError && (
                <p className="text-sm text-destructive bg-destructive/10 p-3 rounded-lg">{inviteError}</p>
              )}
              
              <Button 
                onClick={handleValidateCode}
                disabled={isSubmitting || !inviteCode.trim() || inviteCode.length < 15 || !formData.invite_name?.trim()}
                className="w-full py-6"
                data-testid="validate-code-btn"
              >
                {isSubmitting ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <ArrowRight className="w-5 h-5 mr-2" />
                    {t('validate_code')}
                  </>
                )}
              </Button>
            </div>
          </div>
        );

      case 'invite_confirm':
        // Show family info with privacy warning before joining
        if (!validatedFamily) {
          return (
            <div className="text-center">
              <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          );
        }
        
        return (
          <div className="text-center max-w-md mx-auto">
            <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Users className="w-10 h-10 text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-2">
              {t('join_family_name')} "{validatedFamily.family_name}"
            </h2>
            <p className="text-muted-foreground mb-6">
              {t('created_by_members').replace('{owner}', validatedFamily.owner_name).replace('{count}', validatedFamily.members_count)}
            </p>
            
            {/* Budget mode info */}
            <div className="bg-muted rounded-xl p-4 mb-4">
              <p className="text-sm font-medium mb-2">
                {t('budget_management_mode')}
              </p>
              <p className="text-lg font-bold text-primary">
                {language === 'fr' ? validatedFamily.budget_mode_label_fr : (language === 'pt' ? validatedFamily.budget_mode_label_pt : validatedFamily.budget_mode_label_en)}
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                {language === 'fr' ? validatedFamily.privacy?.description_fr : (language === 'pt' ? validatedFamily.privacy?.description_pt : validatedFamily.privacy?.description_en)}
              </p>
            </div>
            
            {/* Privacy warning */}
            <div className={`rounded-xl p-4 mb-6 ${
              validatedFamily.privacy?.salary_visible 
                ? 'bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800' 
                : 'bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800'
            }`}>
              <p className={`text-sm ${
                validatedFamily.privacy?.salary_visible ? 'text-amber-700 dark:text-amber-400' : 'text-green-700 dark:text-green-400'
              }`}>
                {language === 'fr' ? validatedFamily.privacy?.warning_fr : (language === 'pt' ? validatedFamily.privacy?.warning_pt : validatedFamily.privacy?.warning_en)}
              </p>
            </div>
            
            {/* Member limit error */}
            {memberLimitError && (
              <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-xl p-4 mb-6">
                <p className="text-sm font-medium text-red-700 dark:text-red-400 mb-2">
                  ⚠️ {memberLimitError.message}
                </p>
                {memberLimitError.can_upgrade ? (
                  <p className="text-xs text-red-600 dark:text-red-500">
                    {language === 'fr' 
                      ? `Le propriétaire du foyer peut passer à l'abonnement ${memberLimitError.upgrade_to === 'premium' ? 'Premium' : 'Standard'} (${memberLimitError.upgrade_price}) pour accueillir plus de membres.`
                      : language === 'pt'
                      ? `O proprietário pode atualizar para ${memberLimitError.upgrade_to === 'premium' ? 'Premium' : 'Standard'} (${memberLimitError.upgrade_price}) para adicionar mais membros.`
                      : `The household owner can upgrade to ${memberLimitError.upgrade_to === 'premium' ? 'Premium' : 'Standard'} (${memberLimitError.upgrade_price}) to add more members.`
                    }
                  </p>
                ) : (
                  <p className="text-xs text-red-600 dark:text-red-500">
                    {t('max_member_limit_reached')}
                  </p>
                )}
              </div>
            )}
            
            {/* Confirmation buttons */}
            <div className="space-y-3">
              <Button 
                onClick={handleConfirmJoinFamily}
                disabled={isSubmitting || memberLimitError}
                className="w-full py-6"
                data-testid="confirm-join-btn"
              >
                {isSubmitting ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : memberLimitError ? (
                  <>
                    {t('cannot_join')}
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5 mr-2" />
                    {t('accept_and_join')}
                  </>
                )}
              </Button>
              
              <Button 
                variant="outline"
                onClick={() => {
                  setValidatedFamily(null);
                  setInviteCode('');
                  setMemberLimitError(null);
                  setCurrentStep(prev => prev - 1);
                }}
                className="w-full"
              >
                {t('cancel')}
              </Button>
            </div>
          </div>
        );

      case 'invite_info':
        return (
          <div className="text-center max-w-md mx-auto">
            <div className="w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">
              {t('welcome_to_household').replace('!', ` ${joinedFamily?.family_name || ''} !`)}
            </h2>
            <p className="text-muted-foreground mb-8">
              {t('complete_profile_info')}
            </p>
            
            <div className="space-y-6">
              {/* Salary */}
              <div className="bg-card rounded-2xl p-6 border border-border">
                <label className="text-sm font-medium text-muted-foreground mb-4 block">
                  {t('your_monthly_income')}
                </label>
                <div className="text-4xl font-bold text-primary mb-4">
                  {formData.salary.toLocaleString('fr-FR')} €
                </div>
                <Slider
                  value={[formData.salary]}
                  onValueChange={([value]) => setFormData(prev => ({ ...prev, salary: value }))}
                  min={0}
                  max={15000}
                  step={100}
                />
              </div>
              
              {/* City */}
              <div className="bg-card rounded-2xl p-6 border border-border">
                <label className="text-sm font-medium text-muted-foreground mb-4 block">
                  {t('your_city')}
                </label>
                <Input
                  placeholder={t('example_city')}
                  value={formData.city}
                  onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                  className="text-center text-lg"
                />
              </div>
              
              {/* Family info badge */}
              {joinedFamily && (
                <div className="bg-primary/10 rounded-xl p-4 text-sm">
                  <div className="flex items-center justify-center gap-2 text-primary">
                    <Users className="w-4 h-4" />
                    <span>
                      {t('budget_mode_label')}: {joinedFamily.budget_mode === 'shared' ? t('shared_budget') : joinedFamily.budget_mode === 'hybrid' ? t('hybrid_budget') : t('separate_budgets')}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 'salary':
        return (
          <div className="text-center max-w-md mx-auto">
            <h2 className="text-2xl font-bold mb-6">{t('onboarding_salary')}</h2>
            <div className="bg-card rounded-2xl p-6 border border-border mb-4">
              <label className="text-sm font-medium mb-2 block text-left">
                {t('employment_status')}
              </label>
              <select
                value={formData.income_type}
                onChange={(e) => setFormData(prev => ({ ...prev, income_type: e.target.value }))}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="onboarding-income-type"
              >
                <option value="employed">{t('status_employed')}</option>
                <option value="freelance">{t('status_freelance')}</option>
                <option value="student">{t('status_student')}</option>
                <option value="unemployed">{t('status_unemployed')}</option>
                <option value="other">{t('status_other')}</option>
              </select>
            </div>
            {formData.income_type === 'employed' ? (
              <div className="bg-card rounded-2xl p-8 border border-border">
                <div className="text-5xl font-bold text-primary mb-6">
                  {formData.salary.toLocaleString('fr-FR')} €
                </div>
                <Slider
                  value={[formData.salary]}
                  onValueChange={([value]) => setFormData(prev => ({ ...prev, salary: value }))}
                  min={0}
                  max={15000}
                  step={100}
                  className="mb-4"
                />
                <p className="text-sm text-muted-foreground">
                  {t('monthly_net_income')}
                </p>
              </div>
            ) : (
              <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 rounded-2xl p-6 text-sm text-blue-700 dark:text-blue-300">
                {t('variable_income_hint')}
              </div>
            )}
          </div>
        );

      case 'city':
        return (
          <div className="text-center max-w-md mx-auto">
            <h2 className="text-2xl font-bold mb-8">{t('onboarding_city')}</h2>
            <Input
              placeholder={t('example_city')}
              value={formData.city}
              onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
              className="text-center text-lg py-6"
            />
          </div>
        );

      case 'situation':
        return (
          <div className="text-center max-w-lg mx-auto">
            <h2 className="text-2xl font-bold mb-8">{t('onboarding_situation')}</h2>
            <div className="grid grid-cols-3 gap-4">
              {SITUATIONS.map((situation) => (
                <button
                  key={situation.id}
                  onClick={() => setFormData(prev => ({ 
                    ...prev, 
                    situation: situation.id,
                    family_members: [],
                    budget_mode: 'shared'
                  }))}
                  className={`p-6 rounded-2xl border-2 transition-all ${
                    formData.situation === situation.id
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="text-4xl mb-2">{situation.emoji}</div>
                  <div className="font-medium">{language === 'fr' ? situation.name_fr : language === 'pt' ? (situation.name_pt || situation.name_en) : situation.name_en}</div>
                </button>
              ))}
            </div>
          </div>
        );

      case 'family_setup':
        return (
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold mb-2 text-center">
              {t('household_composition')}
            </h2>
            <p className="text-muted-foreground text-center mb-6">
              {t('add_household_members')}
            </p>

            {/* Family Name */}
            <div className="mb-6">
              <label className="text-sm font-medium mb-2 block">
                {t('household_name')}
              </label>
              <Input
                placeholder={t('example_household_name')}
                value={formData.family_name}
                onChange={(e) => setFormData(prev => ({ ...prev, family_name: e.target.value }))}
              />
            </div>

            {/* Me (owner) */}
            <div className="bg-primary/10 rounded-xl p-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-medium">{user?.name || 'Moi'}</p>
                  <p className="text-sm text-muted-foreground">
                    {t('owner')} • {formData.salary.toLocaleString('fr-FR')}€/mois
                  </p>
                </div>
              </div>
            </div>

            {/* Family Members */}
            <div className="space-y-3 mb-4">
              {formData.family_members.map((member, index) => (
                <div key={member.id} className="bg-card rounded-xl p-4 border border-border">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 space-y-3">
                      <div className="flex gap-2">
                        <Input
                          placeholder={t('first_name')}
                          value={member.name}
                          onChange={(e) => updateFamilyMember(member.id, 'name', e.target.value)}
                          className="flex-1"
                        />
                        <select
                          value={member.role}
                          onChange={(e) => updateFamilyMember(member.id, 'role', e.target.value)}
                          className="px-3 py-2 rounded-md border border-input bg-background"
                        >
                          <option value="adult">{t('adult')}</option>
                          <option value="child">{t('child')}</option>
                        </select>
                      </div>
                      
                      {/* Email pour invitation - adultes seulement */}
                      {member.role === 'adult' && (
                        <div>
                          <label className="text-xs text-muted-foreground">
                            {t('email_for_invitation')}
                          </label>
                          <Input
                            type="email"
                            placeholder="email@exemple.com"
                            value={member.email || ''}
                            onChange={(e) => updateFamilyMember(member.id, 'email', e.target.value)}
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            {t('person_enter_salary_signup')}
                          </p>
                        </div>
                      )}

                      {member.role === 'child' && (
                        <div>
                          <label className="text-xs text-muted-foreground">
                            {t('pocket_money_optional')}
                          </label>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              value={member.pocket_money}
                              onChange={(e) => updateFamilyMember(member.id, 'pocket_money', parseInt(e.target.value) || 0)}
                              className="flex-1"
                            />
                            <span className="text-muted-foreground">€/{t('month')}</span>
                          </div>
                        </div>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-500"
                      onClick={() => removeFamilyMember(member.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <Button variant="outline" className="w-full" onClick={addFamilyMember}>
              <Plus className="w-4 h-4 mr-2" />
              {t('add_member')}
            </Button>

            {/* Summary */}
            {formData.family_members.length > 0 && (
              <div className="mt-6 p-4 bg-muted rounded-xl">
                <p className="font-medium mb-2">{t('summary')}</p>
                <p className="text-sm text-muted-foreground">
                  {t('adults_children_count')
                    .replace('{adults}', formData.family_members.filter(m => m.role === 'adult').length + 1)
                    .replace('{children}', formData.family_members.filter(m => m.role === 'child').length)}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  {t('adult_set_salary_invite')}
                </p>
              </div>
            )}
          </div>
        );

      case 'budget_mode':
        return (
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold mb-2 text-center">
              {t('budget_management_mode')}
            </h2>
            <p className="text-muted-foreground text-center mb-6">
              {t('how_manage_finances')}
            </p>

            <div className="space-y-4 mb-6">
              {BUDGET_MODES.map((mode) => (
                <button
                  key={mode.id}
                  onClick={() => setFormData(prev => ({ ...prev, budget_mode: mode.id }))}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-start gap-4 ${
                    formData.budget_mode === mode.id
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="text-3xl">{mode.emoji}</div>
                  <div>
                    <p className="font-semibold">{language === 'fr' ? mode.name_fr : (language === 'pt' ? mode.name_pt : mode.name_en)}</p>
                    <p className="text-sm text-muted-foreground">{language === 'fr' ? mode.desc_fr : (language === 'pt' ? mode.desc_pt : mode.desc_en)}</p>
                  </div>
                </button>
              ))}
            </div>

            {/* Pot Commun Configuration for Hybrid Mode */}
            {formData.budget_mode === 'hybrid' && (
              <div className="bg-card rounded-xl p-6 border border-border">
                <div className="flex items-center gap-2 mb-4">
                  <PiggyBank className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold">
                    {t('common_pot_config')}
                  </h3>
                </div>
                
                {/* My contribution */}
                <div className="mb-4">
                  <label className="text-sm font-medium mb-2 block">
                    {t('my_monthly_contribution')}
                  </label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={formData.my_pot_contribution}
                      onChange={(e) => setFormData(prev => ({ ...prev, my_pot_contribution: parseInt(e.target.value) || 0 }))}
                      className="flex-1"
                    />
                    <span className="text-muted-foreground">€/{t('month')}</span>
                  </div>
                </div>

                {/* Other members' contributions */}
                {formData.family_members.filter(m => m.role === 'adult').map((member) => (
                  <div key={member.id} className="mb-4">
                    <label className="text-sm font-medium mb-2 block">
                      {t('member_contribution').replace('{name}', member.name || t('member'))}
                    </label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={member.pot_contribution}
                        onChange={(e) => updateFamilyMember(member.id, 'pot_contribution', parseInt(e.target.value) || 0)}
                        className="flex-1"
                      />
                      <span className="text-muted-foreground">€/{t('month')}</span>
                    </div>
                  </div>
                ))}

                {/* Total pot commun */}
                <div className="p-4 bg-primary/10 rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    {t('total_monthly_pot')}
                  </p>
                  <p className="text-2xl font-bold text-primary">
                    {getTotalPotCommun().toLocaleString('fr-FR')}€
                  </p>
                </div>
              </div>
            )}
          </div>
        );

      case 'child_welcome':
        // If there's a member limit error, show error instead of welcome
        if (memberLimitError) {
          return (
            <div className="text-center max-w-md mx-auto">
              <div className="w-24 h-24 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-6 shadow-lg">
                <span className="text-5xl">😕</span>
              </div>
              <h2 className="text-2xl font-bold mb-4 text-red-600 dark:text-red-400">
                {t('oops_cannot_join')}
              </h2>
              <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-xl p-4 mb-6">
                <p className="text-sm font-medium text-red-700 dark:text-red-400 mb-2">
                  {memberLimitError.message}
                </p>
                {memberLimitError.can_upgrade ? (
                  <p className="text-xs text-red-600 dark:text-red-500">
                    {language === 'fr' 
                      ? `Demande à tes parents de passer à l'abonnement ${memberLimitError.upgrade_to === 'premium' ? 'Premium' : 'Standard'} pour que tu puisses rejoindre le foyer.`
                      : language === 'pt'
                      ? `Pede aos teus pais para atualizarem para ${memberLimitError.upgrade_to === 'premium' ? 'Premium' : 'Standard'} para poderes juntar-te.`
                      : `Ask your parents to upgrade to ${memberLimitError.upgrade_to === 'premium' ? 'Premium' : 'Standard'} so you can join the household.`
                    }
                  </p>
                ) : (
                  <p className="text-xs text-red-600 dark:text-red-500">
                    {t('max_member_limit_reached')}
                  </p>
                )}
              </div>
              <Button 
                variant="outline"
                onClick={() => {
                  // Navigate to landing page/logout
                  window.location.href = '/';
                }}
                className="w-full"
              >
                {t('back_to_home')}
              </Button>
            </div>
          );
        }
        
        return (
          <div className="text-center max-w-md mx-auto">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center mx-auto mb-6 shadow-lg">
              <span className="text-5xl">🎉</span>
            </div>
            <h2 className="text-3xl font-bold mb-2">
              {t('welcome_name').replace('{name}', validatedFamily?.recipient_name || joinedFamily?.recipient_name || user?.name || '')}
            </h2>
            <p className="text-muted-foreground mb-8">
              {t('now_part_of_household').replace('{name}', joinedFamily?.family_name || validatedFamily?.family_name)}
            </p>
            
            <div className="space-y-4 mb-8">
              <div className="bg-gradient-to-r from-pink-50 to-purple-50 dark:from-pink-900/20 dark:to-purple-900/20 rounded-2xl p-6">
                <div className="text-5xl mb-3">💰</div>
                <h3 className="font-bold text-lg mb-2">
                  {t('pocket_money')}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t('parents_can_give_pocket_money')}
                </p>
              </div>
              
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-3">
                  <div className="text-2xl mb-1">📊</div>
                  <p className="text-xs text-muted-foreground">
                    {t('track_spending')}
                  </p>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-3">
                  <div className="text-2xl mb-1">🎯</div>
                  <p className="text-xs text-muted-foreground">
                    {t('objectives')}
                  </p>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-3">
                  <div className="text-2xl mb-1">🏆</div>
                  <p className="text-xs text-muted-foreground">
                    {t('challenges')}
                  </p>
                </div>
              </div>
            </div>
            
            <Button 
              size="lg" 
              className="w-full text-lg py-6" 
              onClick={handleCompleteOnboarding}
              disabled={isSubmitting}
              data-testid="child-start-btn"
            >
              {isSubmitting ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <Sparkles className="w-5 h-5 mr-2" />
              )}
              {t('lets_go')}
            </Button>
          </div>
        );

      case 'personality':
        return (
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold mb-8">{t('onboarding_personality')}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {AI_PERSONALITIES.map((personality) => (
                <button
                  key={personality.id}
                  onClick={() => setFormData(prev => ({ ...prev, ai_personality: personality.id }))}
                  className={`p-4 rounded-2xl border-2 transition-all ${
                    formData.ai_personality === personality.id
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="text-3xl mb-2">{personality.emoji}</div>
                  <div className="font-medium text-sm">{language === 'fr' ? personality.name_fr : language === 'pt' ? (personality.name_pt || personality.name_en) : personality.name_en}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {language === 'fr' ? personality.desc_fr : language === 'pt' ? (personality.desc_pt || personality.desc_en) : personality.desc_en}
                  </div>
                </button>
              ))}
            </div>
          </div>
        );

      case 'finish':
        return (
          <div className="text-center max-w-md mx-auto">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold mb-4">
              {t('all_set')}
            </h2>
            <p className="text-muted-foreground mb-8">
              {language === 'fr' 
                ? 'Ton profil est configuré. Tu peux maintenant commencer à gérer tes finances.'
                : language === 'pt'
                ? 'O teu perfil está configurado. Podes começar a gerir as tuas finanças.'
                : 'Your profile is set up. You can now start managing your finances.'
              }
            </p>
            
            {/* Summary */}
            <div className="bg-card rounded-xl p-4 text-left border border-border space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('income_label')}</span>
                <span className="font-medium">{formData.salary.toLocaleString('fr-FR')}€</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('city')}</span>
                <span className="font-medium">{formData.city || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('situation')}</span>
                <span className="font-medium">{SITUATIONS.find(s => s.id === formData.situation)?.[language === 'fr' ? 'name_fr' : language === 'pt' ? 'name_pt' : 'name_en']}</span>
              </div>
              {formData.situation !== 'single' && (
                <>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('household_label')}</span>
                    <span className="font-medium">{formData.family_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('budget_mode_label')}</span>
                    <span className="font-medium">{BUDGET_MODES.find(m => m.id === formData.budget_mode)?.[language === 'fr' ? 'name_fr' : language === 'pt' ? 'name_pt' : 'name_en']}</span>
                  </div>
                  {formData.budget_mode === 'hybrid' && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('common_pot')}</span>
                      <span className="font-medium">{getTotalPotCommun().toLocaleString('fr-FR')}€/mois</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const isNextDisabled = () => {
    const step = steps[currentStep];
    switch (step) {
      case 'city':
        return !formData.city;
      case 'family_setup':
        return !formData.family_name;
      default:
        return false;
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Progress bar */}
      <div className="h-1 bg-muted flex-shrink-0">
        <div 
          className="h-full bg-primary transition-all duration-300"
          style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
        />
      </div>

      {/* Content - Scrollable area */}
      <div className="flex-1 overflow-y-auto">
        <div className="min-h-full flex items-center justify-center p-4 py-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="w-full max-w-2xl"
            >
              {renderStep()}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Navigation - Fixed at bottom */}
      <div className="p-4 border-t border-border bg-background flex-shrink-0">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={handleBack}
            disabled={currentStep === 0}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('back')}
          </Button>

          <div className="flex gap-1">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentStep ? 'bg-primary w-6' : index < currentStep ? 'bg-primary' : 'bg-muted'
                }`}
              />
            ))}
          </div>

          {currentStep === steps.length - 1 ? (
            <Button onClick={handleFinish} disabled={isSubmitting}>
              {isSubmitting ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              {t('finish')}
            </Button>
          ) : (
            <Button onClick={handleNext} disabled={isNextDisabled()}>
              {t('next')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
